/**
 * The rest the German working-hours act (ArbZG §4) asks of a working day: 30 minutes beyond six
 * hours of work, 45 beyond nine, and no stretch of more than six hours without a break. The
 * thresholds are exclusive — six hours exactly needs no break.
 */

/** The working time, in a day or in a row, beyond which 30 minutes of rest are due. */
export const WORK_SECONDS_BEFORE_BREAK = 6 * 60 * 60;

/** The working time in a day beyond which 45 minutes of rest are due. */
export const WORK_SECONDS_BEFORE_LONG_BREAK = 9 * 60 * 60;

const BREAK_SECONDS = 30 * 60;
const LONG_BREAK_SECONDS = 45 * 60;

/**
 * The rest a day's working time requires: none up to six hours, 30 minutes beyond six, 45 beyond
 * nine.
 *
 * @param workSeconds - The day's working time, breaks not included.
 * @returns The rest the day has to hold, in seconds.
 */
export function getRequiredBreakSeconds(workSeconds: number): number {
	if (workSeconds > WORK_SECONDS_BEFORE_LONG_BREAK) return LONG_BREAK_SECONDS;
	if (workSeconds > WORK_SECONDS_BEFORE_BREAK) return BREAK_SECONDS;
	return 0;
}

/** The buffer an organisation starts with, until it sets its own. */
export const DEFAULT_AUTOMATIC_BREAK_BUFFER_MINUTES = 15;

/**
 * The working time a day still has to give up as rest so its record meets the act.
 *
 * That is the shortfall — the rest the day's working time requires, less the rest it already
 * holds — unless the day ran only a little past six or nine hours. Within `bufferSeconds` of a
 * threshold the day is cut back to it instead: to six hours, which need no rest, or to nine with
 * 30 minutes of rest. With the default buffer of 15 minutes, six hours fifteen without a break are
 * recorded as six hours of work and fifteen minutes of rest; six hours sixteen give up the full 30.
 *
 * @param workSeconds - The day's working time, breaks not included.
 * @param breakSeconds - The rest the day already holds.
 * @param bufferSeconds - How far past a threshold a day is still cut back to it.
 * @returns The seconds of work to turn into rest; `0` when the day already complies.
 */
export function getMissingBreakSeconds(
	workSeconds: number,
	breakSeconds: number,
	bufferSeconds: number,
): number {
	const candidates = [getRequiredBreakSeconds(workSeconds) - breakSeconds];
	const pastLongBreak = workSeconds - WORK_SECONDS_BEFORE_LONG_BREAK;
	if (pastLongBreak > 0 && pastLongBreak <= bufferSeconds) {
		candidates.push(Math.max(pastLongBreak, BREAK_SECONDS - breakSeconds));
	}
	const pastBreak = workSeconds - WORK_SECONDS_BEFORE_BREAK;
	if (pastBreak > 0 && pastBreak <= bufferSeconds) candidates.push(pastBreak);
	return Math.max(0, Math.min(...candidates));
}
